import React, { useReducer, useContext } from "react";

import gameReducer from "./gameReducer";
import {
  SETUP_GAME_SETTINGS,
  UPDATE_PLAYERS_BIDS,
  RESET_PLAYER_BIDS,
  SUBMIT_WINNING_BID,
  RESET_WINNING_BID,
  SET_TRUMP_SUIT,
  SET_CURRENT_TURN,
  UPDATE_CURRENT_TRICK,
  SET_TRICK_LEADER,
  SET_TRICK_SUIT,
  RESET_CURRENT_TRICK,
  AWARD_TRICK_TO_WINNER,
  AWARD_TRICK_TO_PLAYER,
  UPDATE_INDIVIDUAL_SCORES,
  RESET_TRICK_SUIT,
  UPDATE_SCORE,
  RESET_BIDDING_ROUND,
  RESET_CURRENT_TURN,
  RESET_GAME_SETTINGS,
  PLAY_AGAIN_SAME_SETTINGS,
  SHOW_TRICK_CARD_WINNER,
} from "./gameActions";

const initialState = {
  gameMode: "",
  numPlayers: "4",
  username: "",
  botNames: { P2: "Player 2", P3: "Player 3", P4: "Player 4" },
  winningScore: "11",
  playersInfo: [],
  playersHands: [],
  playerBids: { p1bid: null, p2bid: null, p3bid: null, p4bid: null },
  winningBid: { bid: 0, team: "", player: "", success: false },
  wonTricks: { teamA: [], teamB: [] },
  currentTurn: "",
  trump: null,
  currentTrick: [],
  leader: "",
  trickSuit: "",
  teamAScore: 0,
  teamBScore: 0,
  points: {
    high: { card: "", team: "" },
    low: { card: "", team: "" },
    game: { teamA: 0, teamB: 0, team: null },
    jack: { card: null, team: null },
    us: 0,
    them: 0,
    usFinal: 0,
    themFinal: 0,
  },
  // Cutthroat-only counterparts to wonTricks/teamAScore/teamBScore/points -
  // one running total and one won-tricks bucket per seat instead of per
  // team. Always carries all 4 seats (matching playersInfo's own always-4
  // shape); P4 simply never gets touched in 3-player games.
  wonTricksByPlayer: { P1: [], P2: [], P3: [], P4: [] },
  individualScores: { P1: 0, P2: 0, P3: 0, P4: 0 },
  individualPoints: {
    high: { card: "", player: null },
    low: { card: "", player: null },
    jack: { card: null, player: null },
    game: { scores: { P1: 0, P2: 0, P3: 0, P4: 0 }, player: null },
    pointsWon: { P1: 0, P2: 0, P3: 0, P4: 0 },
    roundScores: { P1: 0, P2: 0, P3: 0, P4: 0 },
    winners: [],
  },
  settingsSubmitted: false,
};

const GameContext = React.createContext();

const GameProvider = ({ children }) => {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  const setupGameSettings = (values) => {
    const { gameMode, numPlayers, username, winningScore } = values;
    dispatch({
      type: SETUP_GAME_SETTINGS,
      payload: { gameMode, numPlayers, username, winningScore },
    });
  };

  const updatePlayerBid = (person, bid) => {
    const player = `p${person}bid`;
    const newBid = bid.charAt(3);
    const bidAmount = bid === "Pass" ? "Pass" : newBid;
    dispatch({ type: UPDATE_PLAYERS_BIDS, payload: { player, bidAmount } });
  };

  const resetPlayerBids = () => {
    dispatch({ type: RESET_PLAYER_BIDS });
  };

  const submitWinningBid = (person, bid, team) => {
    const player = `P${person}`;

    dispatch({
      type: SUBMIT_WINNING_BID,
      payload: { player, bid, team },
    });
  };

  const resetWinningBid = () => {
    dispatch({ type: RESET_WINNING_BID });
  };

  const setTrumpSuit = (trump) => {
    dispatch({ type: SET_TRUMP_SUIT, payload: { trump } });
  };

  const setCurrentTurn = (person) => {
    const player = `P${person}`;

    dispatch({ type: SET_CURRENT_TURN, payload: { player } });
  };

  const updateCurrentTrick = (card) => {
    dispatch({ type: UPDATE_CURRENT_TRICK, payload: { card } });
  };

  const setTrickLeader = (player) => {
    dispatch({ type: SET_TRICK_LEADER, payload: { player } });
  };

  const resetCurrentTrick = () => {
    dispatch({ type: RESET_CURRENT_TRICK });
  };

  const setTrickSuit = (suit) => {
    dispatch({ type: SET_TRICK_SUIT, payload: { suit } });
  };

  const awardTrickToWinner = (team, trick) => {
    dispatch({ type: AWARD_TRICK_TO_WINNER, payload: { team, trick } });
  };

  const awardTrickToPlayer = (player, trick) => {
    dispatch({ type: AWARD_TRICK_TO_PLAYER, payload: { player, trick } });
  };

  const resetTrickSuit = () => {
    dispatch({ type: RESET_TRICK_SUIT });
  };

  const updateScores = (teamA, teamB, points, success) => {
    dispatch({
      type: UPDATE_SCORE,
      payload: { teamA, teamB, points, success },
    });
  };

  const updateIndividualScores = (scores, points, success) => {
    dispatch({
      type: UPDATE_INDIVIDUAL_SCORES,
      payload: { scores, points, success },
    });
  };

  const resetBiddingRound = () => {
    dispatch({ type: RESET_BIDDING_ROUND });
  };

  const resetCurrentTurn = () => {
    dispatch({ type: RESET_CURRENT_TURN });
  };

  const resetGameSettings = () => {
    dispatch({ type: RESET_GAME_SETTINGS });
  };

  const playAgainSameSettings = () => {
    dispatch({ type: PLAY_AGAIN_SAME_SETTINGS });
  };

  const showTrickCardWinner = (updatedTrick) => {
    dispatch({ type: SHOW_TRICK_CARD_WINNER, payload: { updatedTrick } });
  };

  return (
    <GameContext.Provider
      value={{
        ...state,
        setupGameSettings,
        updatePlayerBid,
        resetPlayerBids,
        submitWinningBid,
        setTrumpSuit,
        resetWinningBid,
        setCurrentTurn,
        updateCurrentTrick,
        resetCurrentTrick,
        setTrickLeader,
        awardTrickToWinner,
        awardTrickToPlayer,
        resetTrickSuit,
        setTrickSuit,
        updateScores,
        updateIndividualScores,
        resetBiddingRound,
        resetCurrentTurn,
        resetGameSettings,
        playAgainSameSettings,
        showTrickCardWinner,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

const useGameContext = () => {
  return useContext(GameContext);
};

export { GameProvider, initialState, useGameContext };
